import { deepAssign, get, isFunction, isObject } from "@ky/shared";
import languages from "@ky/locale/dist/languages";
import type { SupportLang, Message } from "@ky/locale/dist/index.messages.national";
import fromEntries from "lodash-es/fromPairs";
import getDeviceInfoSync from "@ky/sdk/dist/getDeviceInfoSync";
import { reactive } from "vue";
import getUrlParams from "@ky/sdk/dist/getUrlParams";

const { uiLocale, supportLangs, messages } = languages;

// 本地文案库在多语言平台已有的 直接替换成多语言平台上的key
const keyTransform: Record<string, string> = {
  feedBack: 'feedback_title',
  creatorServiceCenter: 'creator_service_center',
  send: 'ksalbum_send',
  pic: 'picture_2',
  video: 'photopick_video',
  noMessage: 'noHistoryMessage',
  uploading: 'uploading__',
  deny: 'livelink_jump_refuse',
  accept: "agree",
  "kwaiCut.slogan": "str_kuaiying_create_fun_videos",
}
let isFirst = true;
function t(lang: SupportLang, prefix: string, path: string, ...args: any[]) {
  if (isFirst) {
    isFirst = false;
    console.info("Locale use language", lang);
  }
  const message = messages[lang];
  let fullPath = prefix ? prefix + "." + path : path;
  if (keyTransform[fullPath]) {
    fullPath = keyTransform[fullPath];
  }

  try {
    const result =
      message[fullPath] || get(message, fullPath) || get(message, path);
    if (isFunction(result)) {
      return result(...args);
    } else if (isObject(result)) {
      return path;
    }
    return result || path;
  } catch (e) {
    console.log("error", e);
    return path;
  }

}

type Callback = (lang: string) => void;
export interface SingleText {
  key: string;
  hash: string;
  text: string[];
}
interface K18nMessages {
  timestamp?: number;
  config?: {
      supportHMR: boolean;
  };
  language: string[];
  textList: SingleText[];
}
interface LocaleType {
  lang: SupportLang;
  cb: Callback | null;
  hasLang(newLang: SupportLang): boolean;
  use(newLang: SupportLang, newMessages: any): void;
  set(newLang: SupportLang): void;
  onLangChange(cb: Callback | null): void;
  add(newMessages: { [x: string]: any }): void;
  addMessagesByPlatform(message: K18nMessages): void;
  t(path: any, ...args: any[]): any;
  install(app: {
    config: {
      globalProperties: {
        $Locale: LocaleType;
        $t: (path: any, ...args: any[]) => any;
      };
    };
  }): void;
}
const lang = reactive({ value: 'zh' } as { value: SupportLang });
const Locale = {
  get lang() {
    return lang.value;
  },
  set lang(newLang: SupportLang) {
    lang.value = newLang;
  },
  cb: null as Callback | null,
  hasLang(newLang: SupportLang) {
    return supportLangs.includes(newLang);
  },
  use(newLang: SupportLang, newMessages: any) {
    Locale.lang = newLang;
    if (newMessages) {
      this.add({ [newLang]: newMessages });
    }
    // eslint-disable-next-line no-unused-expressions
    Locale.cb?.(newLang);
  },
  set(newLang: SupportLang) {
    // 精确匹配
    if (Locale.hasLang(newLang)) {
      Locale.lang = newLang;
      return;
    }
    
    // 大小写不敏感的匹配（ zh-tw 匹配 zh-TW，pt-br 匹配 pt-BR）
    const normalizedLang = supportLangs.find(
      (lang) => lang.toLowerCase() === newLang.toLowerCase()
    );
    if (normalizedLang) {
      Locale.lang = normalizedLang;
      return;
    }
    
    const [mainLang, area] = newLang.split("-");
    const matchedLang = supportLangs.find((lang) => lang.startsWith(mainLang));
    if (matchedLang) {
      Locale.lang = matchedLang;
    } else {
      console.warn(`${newLang} is not supported, fallback to default language`);
    }
  },
  onLangChange(cb: Callback | null) {
    Locale.cb = cb;
  },
  add(newMessages: { [x: string]: any }) {
    deepAssign(messages, newMessages);
  },
  addMessagesByPlatform(message: K18nMessages) {
    const { language, textList } = message;
    const newMessages: Message = {};
    language.forEach((lang, index) => {
      const message = fromEntries(
        textList.map((item) => [item.key, item.text[index]])
      );
      newMessages[lang] = message;
    });
    Locale.add(newMessages);
  },
  t(path: any, ...args: any[]) {
    return t(Locale.lang, "", path, ...args);
  },
} as LocaleType;

const deviceInfo = getDeviceInfoSync();
let language = 'zh' as SupportLang;


(async () => {
  let appInfo: any = {};
  try {
    const appInfoResult = await getAppInfo("all");
    // iOS 返回 string，Android 直接返回对象
    if (typeof appInfoResult === 'string') {
      appInfo = JSON.parse(appInfoResult || "{}");
    } else if (typeof appInfoResult === 'object' && appInfoResult !== null) {
      appInfo = appInfoResult;
    } else {
      appInfo = {};
    }
  } catch (error) {
    console.warn('Failed to parse appInfo:', error);
    appInfo = {};
  }

  const paramLang = getUrlParams("lang");
  if (paramLang) {
    language = paramLang as SupportLang;
  } else if (process.env.CODE_ENV === "spg" || deviceInfo.isOverseas) {
    language = (appInfo.appLanguage || 'en') as SupportLang;
  }
  Locale.set(language);
})();

Locale.addMessagesByPlatform(uiLocale);
Locale.install = function (app: {
  config: {
    globalProperties: {
      $Locale: LocaleType;
      $t: (path: any, ...args: any[]) => any;
    };
  };
}) {
  app.config.globalProperties.$Locale = Locale;
  app.config.globalProperties.$t = Locale.t;
};

export default Locale;
